import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ClockCounterClockwise, ArrowsClockwise, ChatCircle, Download,
} from "@phosphor-icons/react";
import { AuditRow } from "@/components/AuditTrailView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShipmentAuditTimeline — unified per-shipment timeline (audit_events + legacy).
 *
 * Embedded as the "Audit Trail" tab on ShipmentDetail. Surfaces every event
 * that ever touched the shipment: creation, lifecycle changes, logger upload
 * / attach / replay, facility arrival/departure, excursions, corrective
 * actions, alert lifecycle, notification dispatch attempts, and operator
 * comments. Investigators can drop a free-form comment that lands as
 * `user.comment` for downstream evidence.
 */
export default function ShipmentAuditTimeline({ shipmentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const fetchTimeline = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit/shipment/${shipmentId}`, { params: { limit: 500 } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit timeline");
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const submitComment = async () => {
    const txt = comment.trim();
    if (!txt) return;
    setPosting(true);
    try {
      await axios.post(`${API}/audit/comment`, { shipment_id: shipmentId, comment: txt });
      setComment("");
      toast.success("Comment logged");
      fetchTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log comment");
    } finally {
      setPosting(false);
    }
  };

  const exportCsv = async () => {
    // Use axios so the global interceptor injects the auth token from the
    // same source the rest of the app uses (auth_token). The previous
    // hand-rolled `fetch` read the wrong localStorage key and silently
    // saved the JSON auth error as the "CSV".
    try {
      const r = await axios.get(`${API}/audit/export.csv`, {
        params: { shipment_id: shipmentId, limit: 5000 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipment-audit-${shipmentId.slice(0, 8)}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit trail exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="shipment-audit-timeline">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={16} weight="duotone" className="text-violet-400" />
            <span className="text-sm font-semibold text-zinc-100">Audit Trail</span>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              {items.length} events · append-only
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"
              onClick={exportCsv} disabled={items.length === 0}
              className="border-zinc-700 hover:bg-zinc-800 text-xs h-7"
              data-testid="shipment-audit-export">
              <Download size={11} className="mr-1" /> Export
            </Button>
            <Button variant="outline" size="sm"
              onClick={fetchTimeline}
              className="border-zinc-700 hover:bg-zinc-800 text-xs h-7"
              data-testid="shipment-audit-refresh">
              <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        {/* Inline comment box — investigator-authored, audited as user.comment */}
        <div className="flex items-start gap-2 mb-3">
          <ChatCircle size={14} className="text-violet-400 mt-2" />
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Add an investigator comment (becomes a permanent audit entry)…"
            className="bg-zinc-950 border-zinc-800 text-xs text-zinc-200 min-h-[60px] resize-none"
            data-testid="audit-comment-input" />
          <Button onClick={submitComment} disabled={posting || !comment.trim()}
            size="sm" className="bg-violet-500 hover:bg-violet-400 text-zinc-50 text-xs h-9"
            data-testid="audit-comment-submit">
            {posting ? "Logging…" : "Log"}
          </Button>
        </div>

        {/* Timeline */}
        {loading && items.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-8 animate-pulse">Loading timeline…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-8">
            No events yet — actions on this shipment will appear here.
          </p>
        ) : (
          <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
            {items.map((e) => (
              <AuditRow key={e.id} event={e} compact />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
